import { Transaction, Aircraft } from '../types/intellijetnet'

// Monthly data point for chart visualization
export interface MonthlyMarketData {
  month: string // e.g., "Jan 2024"
  monthKey: string // e.g., "2024-01" for sorting
  listingsCount: number // Number of aircraft for sale in this month
  soldCount: number // Number of aircraft sold in this month
}

export interface MarketStatistics {
  averageSalePrice: number | null
  numberOfSales: number
  averageTimeOnMarket: number | null // in days
  historicalMonths: number
  monthlyData: MonthlyMarketData[] // Monthly data for chart
}

/**
 * Generate an array of month keys for the historical period
 * @param historicalMonths - Number of months to look back
 * @returns Array of month keys in format "YYYY-MM" from oldest to newest
 */
function generateMonthKeys(historicalMonths: number): string[] {
  const months: string[] = []
  const now = new Date()

  for (let i = historicalMonths - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    months.push(`${year}-${month}`)
  }

  return months
}

/**
 * Format a month key to a display string
 * @param monthKey - Month key in format "YYYY-MM"
 * @returns Display string like "Jan 2024"
 */
function formatMonthDisplay(monthKey: string): string {
  const [year, month] = monthKey.split('-')
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${monthNames[parseInt(month, 10) - 1]} ${year}`
}

/**
 * Extract month key from a date string
 * @param dateStr - Date string in format "YYYY-MM-DD"
 * @returns Month key in format "YYYY-MM" or null if invalid
 */
function getMonthKeyFromDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  const match = dateStr.match(/^(\d{4})-(\d{2})/)
  return match ? `${match[1]}-${match[2]}` : null
}

/**
 * Get the last day of a month from a month key
 * @param monthKey - Month key in format "YYYY-MM"
 * @returns Date string for the last day of the month "YYYY-MM-DD"
 */
function getMonthEndDate(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number)
  // Create date for first day of next month, then subtract one day
  const lastDay = new Date(year, month, 0).getDate()
  return `${monthKey}-${String(lastDay).padStart(2, '0')}`
}

/**
 * Check if a date is on or before another date
 * @param dateStr - Date to check (YYYY-MM-DD format)
 * @param compareDate - Date to compare against (YYYY-MM-DD format)
 * @returns true if dateStr <= compareDate
 */
function isOnOrBefore(dateStr: string | null | undefined, compareDate: string): boolean {
  if (!dateStr) return false
  return dateStr <= compareDate
}

/**
 * Check if a date is after another date
 * @param dateStr - Date to check (YYYY-MM-DD format)
 * @param compareDate - Date to compare against (YYYY-MM-DD format)
 * @returns true if dateStr > compareDate
 */
function isAfter(dateStr: string | null | undefined, compareDate: string): boolean {
  if (!dateStr) return false
  return dateStr > compareDate
}

/**
 * Calculate market statistics from transaction and aircraft data
 * @param transactions - Array of transactions for the model
 * @param historicalMonths - Number of months to look back
 * @param aircraft - Optional array of aircraft for calculating new listings per month
 * @returns MarketStatistics object with calculated values
 */
export function calculateMarketStatistics(
  transactions: Transaction[],
  historicalMonths: number,
  aircraft?: Aircraft[]
): MarketStatistics {
  // Calculate cutoff date
  const cutoffDate = new Date()
  cutoffDate.setMonth(cutoffDate.getMonth() - historicalMonths)
  const cutoffDateStr = cutoffDate.toISOString().split('T')[0]

  // Filter transactions within the historical period
  const filteredTransactions = transactions.filter(t => {
    const transDate = t.trans_date || t.action_date
    return transDate && transDate >= cutoffDateStr
  })

  // Calculate number of sales
  const numberOfSales = filteredTransactions.length

  // Calculate average sale price (only from transactions with sold_price)
  const transactionsWithPrice = filteredTransactions.filter(t => t.sold_price != null && t.sold_price > 0)
  const averageSalePrice = transactionsWithPrice.length > 0
    ? transactionsWithPrice.reduce((sum, t) => sum + (t.sold_price || 0), 0) / transactionsWithPrice.length
    : null

  // Calculate average time on market (from list_date to trans_date)
  const transactionsWithDates = filteredTransactions.filter(t => {
    const listDate = t.list_date
    const transDate = t.trans_date || t.action_date
    return listDate && transDate
  })

  let averageTimeOnMarket: number | null = null
  if (transactionsWithDates.length > 0) {
    const totalDays = transactionsWithDates.reduce((sum, t) => {
      const listDate = new Date(t.list_date!)
      const transDate = new Date(t.trans_date || t.action_date)
      const diffTime = transDate.getTime() - listDate.getTime()
      const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
      return sum + diffDays
    }, 0)
    averageTimeOnMarket = totalDays / transactionsWithDates.length
  }

  // ====== Calculate Monthly Data for Chart ======
  const monthKeys = generateMonthKeys(historicalMonths)

  // Count sales per month from transactions
  const salesByMonth = new Map<string, number>()
  filteredTransactions.forEach(t => {
    const transDate = t.trans_date || t.action_date
    const monthKey = getMonthKeyFromDate(transDate)
    if (monthKey && monthKeys.includes(monthKey)) {
      salesByMonth.set(monthKey, (salesByMonth.get(monthKey) || 0) + 1)
    }
  })

  // Calculate listings count for each month
  // An aircraft was "on market" during a month if:
  // 1. It was listed on or before the end of that month, AND
  // 2. It either hasn't been sold yet (current for-sale) OR was sold after that month ended
  const monthlyData: MonthlyMarketData[] = monthKeys.map(monthKey => {
    const monthEndDate = getMonthEndDate(monthKey)

    // Count current for-sale aircraft that were listed on or before this month's end
    let listingsCount = 0
    if (aircraft) {
      listingsCount += aircraft.filter(a =>
        isOnOrBefore(a.datelisted, monthEndDate)
      ).length
    }

    // Count sold aircraft that were on market during this month
    // (listed on or before month end, AND sold after month end)
    listingsCount += transactions.filter(t =>
      isOnOrBefore(t.list_date, monthEndDate) && isAfter(t.trans_date, monthEndDate)
    ).length

    return {
      month: formatMonthDisplay(monthKey),
      monthKey,
      listingsCount,
      soldCount: salesByMonth.get(monthKey) || 0
    }
  })

  return {
    averageSalePrice,
    numberOfSales,
    averageTimeOnMarket,
    historicalMonths,
    monthlyData
  }
}

